import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  makeStyles,
  TableRow,
  Typography,
  InputAdornment,
  colors,
  IconButton,
} from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import Divider from "@material-ui/core/Divider";
import ReplayIcon from "@material-ui/icons/Replay";
import SearchIcon from "@material-ui/icons/Search";

import { DefaultButton, OutlineButton } from "../../../components/Buttons";
import TableData, {
  StyledTableCell,
  StyledTableRow,
} from "../../../components/TableData";
import Paginator, { DataTypeEnum } from "../../../../../models/Paginator";
import BusinessBranch from "../../../../../models/BusinessBranch";
import { PageTitle } from "../../../../../components/page-title/PageTitle";
import { InputField } from "../../../../../components/input-field/InputField";
import ICIPAdminService from "../../../../../services/ICIPAdminService";
import Business from "../../../../../models/Business";
import { NotificationDispatchContext } from "../../../../../context/NotificationContext";
import { CustomTooltip } from "../../../components/Tooltip";
import DynamicPaginationActions from "../../../components/DynamicPaginationActions";
import ActivitySchema from "../../../../../models/ActivitySchema";

interface BusinessBranchOverviewProps {
  business: Business;
}

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    subMain: {
      padding: "32px 32px",
    },
    tableWrapper: {
      overflow: "hidden",

      "& table": {
        fontSize: 14,
        overflow: "hidden",
        // borderTop: "1px solid #e8e8e8",

        "& thead tr th:first-child": {
          padding: "14px 32px 14px 34px",
          textAlign: "left",
        },
        "& thead tr th": {
          padding: "14px 32px 14px 12px",
          textAlign: "left",
          borderBottom: "1px solid #e8e8e8",
        },
        "& thead tr th:last-child": {
          textAlign: "right",
        },
        "& tbody tr td:last-child": {
          textAlign: "right",
        },
        "& tbody tr td": {
          padding: "14px 32px 14px 12px",
          textAlign: "left",
          cursor: "pointer",
          borderBottom: "1px solid #e8e8e8",
        },
        "& tbody tr td:first-child": {
          padding: "14px 32px 14px 34px",
          fontWeight: 700,
        },
        "& tbody tr:hover": {
          "& td": {
            backgroundColor: theme.palette.grey[50],
          },
        },
        "& tbody tr:nth-child(odd)": {
          "& td": {
            backgroundColor: theme.palette.common.white,
          },
        },
        "& tbody tr:nth-child(even)": {
          "& td": {
            backgroundColor: "#fafafa",
          },
        },
      },
    },
    noBorder: {
      border: 0,
      fontSize: 14,
      paddingTop: theme.spacing(4),
    },
    tableAction: {
      "& button": {
        padding: "4px 10px",
        fontSize: 12,
      },
    },
    actionItem: {
      marginLeft: 20,
    },
    searchWrapper: {
      display: "flex",
      alignItems: "center",

      "& input": {
        padding: "10px 12px",
        borderRadius: "10px !important",
      },
    },
    searchHeaderWrapper: {
      display: "flex",
      alignItems: "center",
      paddingLeft: "20px",
      paddingRight: theme.spacing(4),
    },
    paginatorWrapper: {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      marginTop: "14px",

      "& hr": {
        marginTop: "14px",
        height: "24px",
      },
    },
    extraAction: {
      marginRight: 4,
      color: colors.grey[800],
    },
  }),
  { name: "wallet-overview-tab-styles" }
);

enum LoadingStatusEnum {
  loading,
  failed,
  success,
  paginating,
}

interface BranchTabData {
  original: Paginator;
  filtered: Paginator;
}

export default function BusinessBranchOverview(
  props: BusinessBranchOverviewProps
) {
  const classes = useStyles(props);
  const intl = useIntl();
  // State
  const [query, setQuery] = useState("");
  const [business, setBusiness] = useState(props.business);
  const [data, setData] = useState((): BranchTabData => null);
  const [loadingStatus, setLoadingStatus] = useState(LoadingStatusEnum.loading);
  // Context
  const dispatch = useContext(NotificationDispatchContext);

  const dispatchError = (response: any) => {
    let message = intl.formatMessage({ id: "sharedScreen.unknownError" });
    if (response.data && response.data.data && response.data.data["message"]) {
      message = response.data.data["message"];
    }

    dispatch({
      data: {
        type: "error",
        message: message,
      },
    });
  };

  const fetchBusinessBranches = (
    page: number = 1,
    paginating: boolean = false
  ) => {
    dispatch({ data: { message: null } });
    if (paginating) {
      setLoadingStatus(LoadingStatusEnum.paginating);
    } else {
      setLoadingStatus(LoadingStatusEnum.loading);
    }

    ICIPAdminService.businessBranches(business.id, page).then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let paginator = Paginator.fromJson(
          JSON.parse(JSON.stringify(response.data.data)),
          DataTypeEnum.branches
        );

        setData({
          original: paginator,
          filtered: paginator,
        });
        setLoadingStatus(LoadingStatusEnum.success);
      } else {
        dispatchError(response);
        setLoadingStatus(LoadingStatusEnum.failed);
      }
    });
  };

  const inviteBranchManagerModalHandler = (
    state: boolean,
    branch?: BusinessBranch
  ) => {};

  const renderActivitiesTableHeader = () => {
    return (
      <TableRow>
        <StyledTableCell>
          <FormattedMessage id="sharedScreen.name" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.address" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.latitude" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.longitude" />
        </StyledTableCell>
        <StyledTableCell align="right">
          <FormattedMessage id="sharedScreen.action" />
        </StyledTableCell>
      </TableRow>
    );
  };

  const renderBranchesTableRows = () => {
    return data.filtered.items
      .filter((item) => {
        let branch: BusinessBranch = item as BusinessBranch;

        if (query === "") {
          return branch;
        } else if (
          branch.fullAddress().toLowerCase().includes(query.toLowerCase()) ||
          branch.fullName().toLowerCase().includes(query.toLowerCase())
        ) {
          return branch;
        }
      })
      .map((item, index) => {
        let branch = item as BusinessBranch;

        return (
          <StyledTableRow key={index}>
            <StyledTableCell component="td" scope="row">
              <CustomTooltip title={branch.fullName()} arrow>
                <Typography component="span">{branch.fullName()}</Typography>
              </CustomTooltip>
            </StyledTableCell>
            <StyledTableCell component="td" scope="row">
              {branch.address}
            </StyledTableCell>
            <StyledTableCell component="td" scope="row">
              {branch.latitude}
            </StyledTableCell>
            <StyledTableCell component="td" scope="row">
              {branch.longitude}
            </StyledTableCell>
            <StyledTableCell align="right" component="td" scope="row">
              <Box className={classes.tableAction}>
                <DefaultButton
                  onClick={(_) => inviteBranchManagerModalHandler(true, branch)}
                >
                  <Typography component="span" noWrap>
                    <FormattedMessage id="sharedScreen.inviteManager" />
                  </Typography>
                </DefaultButton>
              </Box>
            </StyledTableCell>
          </StyledTableRow>
        );
      });
  };

  useEffect(() => {
    fetchBusinessBranches(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageTitle
        withAppTitle={false}
        title={`${intl.formatMessage({
          id: "sharedScreen.admin",
        })} - ${intl.formatMessage({
          id: "sharedScreen.businessBranches",
        })}`}
      />
      <Box component="div" className={classes.root}>
        <Box component="div">
          <Box component="div" display="flex" alignItems="center" mr={4}>
            <Box component="div" style={{ flexGrow: 1 }}>
              <Typography
                variant="h4"
                component="h4"
                className={classes.subMain}
              >
                <FormattedMessage id="sharedScreen.businessBranches" />
              </Typography>
            </Box>
            {loadingStatus === LoadingStatusEnum.loading ? (
              <CircularProgress
                color="secondary"
                disableShrink={false}
                size={20}
                thickness={4}
              />
            ) : loadingStatus === LoadingStatusEnum.failed ? (
              <OutlineButton onClick={(_) => fetchBusinessBranches(1, false)}>
                <FormattedMessage id="sharedScreen.retry" />
              </OutlineButton>
            ) : (
              <></>
            )}
          </Box>
          {loadingStatus === LoadingStatusEnum.success ||
          loadingStatus === LoadingStatusEnum.paginating ? (
            <>
              <Box component="div" className={classes.searchHeaderWrapper}>
                <Box component="div" className={classes.paginatorWrapper}>
                  <Box>
                    <CustomTooltip
                      title={intl.formatMessage({ id: "sharedScreen.refresh" })}
                      arrow
                    >
                      <IconButton
                        disabled={false}
                        className={classes.extraAction}
                        onClick={(_) => fetchBusinessBranches(1, false)}
                      >
                        <ReplayIcon fontSize="small" />
                      </IconButton>
                    </CustomTooltip>
                  </Box>
                  <Divider orientation="vertical" variant="middle" flexItem />
                  <Box ml={2}>
                    <DynamicPaginationActions
                      loading={loadingStatus === LoadingStatusEnum.paginating}
                      enabled={
                        query.length === 0 &&
                        loadingStatus !== LoadingStatusEnum.paginating
                      }
                      pageNumber={data.filtered.page}
                      perPage={data.filtered.per_page}
                      totalPages={data.filtered.total}
                      handlePrevAction={
                        data.filtered.prev_page !== null
                          ? () => {
                              fetchBusinessBranches(
                                data.filtered.page - 1,
                                true
                              );
                            }
                          : null
                      }
                      handleNextAction={
                        data.filtered.next_page !== null
                          ? () => {
                              fetchBusinessBranches(
                                data.filtered.page + 1,
                                true
                              );
                            }
                          : null
                      }
                    />
                  </Box>
                </Box>
                <Box component="div" className={classes.searchWrapper}>
                  <Box component="div" style={{ width: "300px" }}>
                    <InputField
                      fullWidth
                      id="search"
                      name="search"
                      placeholder={intl.formatMessage({
                        id: "sharedScreen.research",
                      })}
                      type="text"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      endAdornment={
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      }
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                component="div"
                className={classes.tableWrapper}
                mb={4}
                mt={2}
              >
                <TableData
                  rows={renderBranchesTableRows()}
                  header={renderActivitiesTableHeader()}
                />
              </Box>
            </>
          ) : (
            <></>
          )}
        </Box>
      </Box>
    </>
  );
}
